// src/features/faq/components/BestClientsFAQ.tsx

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ChevronDown, ChevronUp, Crown } from 'lucide-react';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface BestClientsFAQProps {
  onNavigate?: (path: string) => void;
}

const BestClientsFAQ: React.FC<BestClientsFAQProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleQuestion = useCallback((questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  }, []);

  const navigateTo = useCallback((path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  }, [navigate, onNavigate]);

  const faqs: FAQItem[] = [
    {
      id: 'best-clients-purpose',
      question: '¿Para qué sirve la sección de Mejores Clientes?',
      answer: (
        <div>
          <p>La sección de Mejores Clientes te permite:</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>Identificar a tus clientes más leales según el criterio que elijas (saldo de puntos, valor histórico o frecuencia de visitas).</li>
            <li>Reconocer a tus clientes VIP (Top 3 con iconos especiales de Trofeo y Top 5 con Medallas en la primera página).</li>
            <li>Ver cuándo fue su última visita para reactivar a los que no han regresado.</li>
            <li>Contactarlos directamente para ofrecerles promociones exclusivas.</li>
          </ul>
          <p className="mt-2 text-content-2">Esta información es valiosa para implementar estrategias de fidelización dirigidas a tus mejores clientes, quienes representan el mayor valor para tu negocio.</p>
          <div className="mt-2">
            <button onClick={() => navigateTo('/dashboard/best-clients')} className="inline-flex items-center text-sm text-accent hover:text-blue-800 dark:text-blue-300">
              <Trophy className="h-4 w-4 mr-1.5" />
              Ir a Mejores Clientes
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'best-clients-ranking',
      question: '¿Cómo se determina el ranking de mejores clientes?',
      answer: (
        <div>
          <p>De forma predeterminada, el ranking ordena a tus clientes por su <strong>saldo de puntos actual</strong> (de mayor a menor). Pero puedes cambiar el criterio con el selector que está en la parte superior de la página:</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li><strong>Saldo Actual:</strong> los puntos (o el saldo en cashback) que el cliente tiene disponibles hoy. Baja cuando canjea una recompensa.</li>
            <li><strong>Valor Histórico:</strong> el total de puntos que ha ganado en toda su historia. No baja al canjear; ideal para reconocer la lealtad acumulada.</li>
            <li><strong>Frecuencia de Visitas:</strong> ordena por la cantidad de visitas registradas.</li>
          </ul>
          <p className="mt-2">También puedes acotar el periodo a considerar: todo el tiempo, últimos 30 días, 90 días o 12 meses.</p>
          <p className="mt-2 text-content-2">Los primeros lugares reciben distintivos visuales: 🏆 Oro, Plata y Bronce para el Top 3, y 🏅 Medalla para los puestos 4 y 5 (visibles en la <strong>primera página</strong> de la lista).</p>
          <div className="mt-3 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Consejo:</strong> Los puntos se acumulan principalmente por visitas, pero también puedes asignar puntos manualmente a través de la función <button onClick={() => navigateTo('/dashboard/recharge-points')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Gestionar Puntos</button>
              <span className="ml-1 text-xs text-amber-700 font-medium inline-flex items-center">(<Crown className="h-3 w-3 mr-0.5 text-yellow-600 dark:text-yellow-400" />disponible en el plan PRO)</span>.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'best-clients-export',
      question: '¿Cómo puedo exportar la lista de mejores clientes?',
      answer: (
        <div>
          <p>Para exportar la lista de tus mejores clientes a un archivo Excel:</p>
          <ol className="list-decimal ml-5 mt-2 space-y-1">
            <li>Ve a la página de <button onClick={() => navigateTo('/dashboard/best-clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mejores Clientes</button> en el menú lateral.</li>
            <li>Haz clic en el botón <strong>"Descargar Reporte"</strong> que se encuentra en la parte superior de la sección.</li>
            <li>Se abrirá una ventana donde puedes <strong>elegir qué columnas incluir</strong>; al confirmar, se descarga el archivo Excel (<code>.xlsx</code>).</li>
          </ol>
          <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
            <p className="text-sm text-amber-700">
              <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Función PRO:</strong> La exportación a Excel está disponible exclusivamente para usuarios con suscripción PRO. Actualiza tu plan para acceder a esta funcionalidad.
            </p>
          </div>
          <p className="mt-2 text-content-2">Puedes incluir, entre otras columnas: ranking, nombre, teléfono, email, cumpleaños, visitas, días sin venir, fecha de última visita, puntos o saldo, etiquetas y sucursal de registro (según los datos disponibles de cada cliente).</p>
        </div>
      )
    },
    {
      id: 'best-clients-search',
      question: '¿Cómo busco un cliente específico en la lista?',
      answer: (
        <div>
          <p>Para buscar un cliente específico en la lista de mejores clientes:</p>
          <ol className="list-decimal ml-5 mt-2 space-y-1">
            <li>Dirígete a la sección de <button onClick={() => navigateTo('/dashboard/best-clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mejores Clientes</button>.</li>
            <li>Utiliza la barra de búsqueda ubicada en la parte superior de la lista.</li>
            <li>Puedes ingresar el nombre o el número telefónico del cliente que deseas encontrar.</li>
            <li>Los resultados se filtrarán automáticamente en la lista a medida que escribes.</li>
          </ol>
          <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
            <p className="text-sm text-amber-700">
              <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Consejo PRO:</strong> La búsqueda avanzada de clientes, con más filtros y opciones, es una característica exclusiva para usuarios PRO. Actualiza tu plan para una búsqueda más potente.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'best-clients-strategies',
      question: '¿Qué estrategias puedo implementar con mis mejores clientes?',
      answer: (
        <div>
          <p>Identificar a tus mejores clientes te abre la puerta a múltiples estrategias de fidelización y marketing. Algunas ideas incluyen:</p>
          <ul className="list-disc ml-5 mt-2 space-y-2">
            <li>
              <strong>Programas VIP Exclusivos:</strong> Crea un nivel especial para ellos con beneficios únicos como descuentos mayores, acceso anticipado a novedades o atención prioritaria.
            </li>
            <li>
              <strong>Recompensas Personalizadas:</strong> Utiliza la sección de <button onClick={() => navigateTo('/dashboard/rewards')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Recompensas</button> para diseñar premios especiales que se alineen con los gustos o el historial de tus mejores clientes.
            </li>
            <li>
              <strong>Comunicación Directa y Personal:</strong> Usa las <button onClick={() => navigateTo('/dashboard/campaigns')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Campañas</button> (función PRO) para enviarles mensajes segmentados, ofertas especiales o agradecimientos.
            </li>
            <li>
              <strong>Eventos Exclusivos:</strong> Invítalos a eventos especiales (virtuales o presenciales) solo para este grupo selecto, generando un sentido de comunidad.
            </li>
            <li>
              <strong>Programas de Referidos Mejorados:</strong> Incentívalos con bonificaciones extra si refieren nuevos clientes a tu negocio.
            </li>
            <li>
              <strong>Solicitar Feedback Valioso:</strong> Al ser tus clientes más leales, su opinión sobre nuevos productos o servicios puede ser extremadamente útil.
            </li>
          </ul>
          <div className="mt-3 bg-green-50 dark:bg-green-500/10 p-3 rounded-md border border-green-200 dark:border-green-500/30">
            <p className="text-sm text-green-700 dark:text-green-300">
              <strong>Dato importante:</strong> Según estudios de marketing, retener a un cliente existente cuesta hasta 5 veces menos que adquirir uno nuevo, y tus mejores clientes suelen ser responsables de una gran parte de tus ingresos (Principio de Pareto 80/20).
            </p>
          </div>
        </div>
      )
    },
    // La pregunta 'best-clients-contact' ha sido eliminada.
    {
      id: 'best-clients-history',
      question: '¿Cómo veo el historial detallado de un cliente listado aquí?',
      answer: (
        <div>
          <p>Para ver el historial completo de un cliente desde la lista de "Mejores Clientes":</p>
          <ol className="list-decimal ml-5 mt-2 space-y-1">
            <li>Ve a la página de <button onClick={() => navigateTo('/dashboard/best-clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mejores Clientes</button>.</li>
            <li>Localiza al cliente en la lista (puedes usar la búsqueda si es necesario).</li>
            <li>Haz clic en <strong>cualquier parte de la fila</strong> del cliente.</li>
            <li>Serás redirigido a la página de historial de ese cliente específico, donde podrás ver detalles como:</li>
            <ul className="list-disc ml-8 mt-1">
              <li>Su información personal completa.</li>
              <li>Un registro cronológico de todas sus visitas.</li>
              <li>Los puntos ganados en cada interacción.</li>
              <li>Recompensas canjeadas.
              </li>
              <li>Feedback o comentarios que haya proporcionado (si aplica).</li>
            </ul>
          </ol>
          <p className="mt-2 text-content-2">
            Este historial detallado es muy útil para entender los patrones de comportamiento y las preferencias de tus clientes más valiosos.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={faq.id}
          className={`bg-surface rounded-lg shadow-sm overflow-hidden transition-all duration-200 ${
            expandedQuestions.has(faq.id) ? 'shadow-md' : ''
          }`}
          style={{ animationDelay: `${0.4 + index * 0.1}s` }}
        >
          <button
            className="w-full flex justify-between items-start p-4 focus:outline-none"
            onClick={() => toggleQuestion(faq.id)}
          >
            <span className="text-left font-medium text-content">{faq.question}</span>
            <span className="ml-6 flex-shrink-0">
              {expandedQuestions.has(faq.id) ? (
                <ChevronUp className="h-5 w-5 text-content-3" />
              ) : (
                <ChevronDown className="h-5 w-5 text-content-3" />
              )}
            </span>
          </button>
          {expandedQuestions.has(faq.id) && (
            <div className="px-4 pb-4 text-content-2">
              <div className="border-t border-line pt-4">
                {faq.answer}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default BestClientsFAQ;